import React, { useState } from "react";
import { Card, Button, Table, Modal, Alert, Spinner } from "react-bootstrap";
import { IconPlus, IconEdit, IconTrash, IconEye, IconPrinter } from "@tabler/icons-react";
import LayoutAgentRank from "layouts/layout-agent-rank";
import {
  AgentPenaltyProvider,
  useAgentPenalty,
} from "./context/AgentPenaltyContext";
import AgentPenaltyForm from "./components/AgentPenaltyForm";
import PrintAgentPenalty from "./components/PrintAgentPenalty";
import { getContrastColor } from "utils/utils";
import { AgentProvider } from "contexts/v1/masterData/AgentContext";

function AgentRankPenaltyContent() {
  const { agentPenalties, filterOptions, handleSearch, refetch, Pagination } =
    useAgentPenalty();

  const [showModal, setShowModal] = useState(false);
  const [editingPenalty, setEditingPenalty] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingPenalty, setViewingPenalty] = useState(null);
  const [printingPenalty, setPrintingPenalty] = useState(null);

  // Handle create new penalty
  const handleCreate = () => {
    setEditingPenalty(null);
    setShowModal(true);
  };

  // Handle edit penalty
  const handleEdit = (penalty) => {
    setEditingPenalty(penalty);
    setShowModal(true);
  };

  // Handle view penalty details
  const handleView = (penalty) => {
    setViewingPenalty(penalty);
    setShowViewModal(true);
  };

  // Handle print penalty
  const handlePrint = (penalty) => {
    setPrintingPenalty(penalty);
  };

  // Handle delete penalty
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this penalty?")) {
      return;
    }

    try {
      const response = await fetch(`/api/agent-penalty/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      refetch(); // Refresh the data
    } catch (err) {
      console.error("Error deleting penalty:", err);
      alert("Failed to delete penalty. Please try again.");
    }
  };

  // Handle form success
  const handleFormSuccess = () => {
    setShowModal(false);
    refetch(); // Refresh the data
  };

  // Handle form error
  const handleFormError = (error) => {
    console.error("Form error:", error);
    alert("Failed to save penalty. Please try again.");
  };

  // Close modals
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPenalty(null);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewingPenalty(null);
  };

  return (
    <LayoutAgentRank>
      <div className="container-fluid mt-5">
        <h1>AGENT PENALTY MANAGEMENT</h1>

        <div
          style={{
            minHeight: "100vh",
            backgroundColor: "#f9fafb",
            padding: "16px",
          }}
        >
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <h2 style={{ fontSize: "20px", fontWeight: "600" }}>
                  Agent Penalties
                </h2>
                <Button
                  variant="primary"
                  onClick={handleCreate}
                  disabled={agentPenalties.loading}
                >
                  <IconPlus size={16} className="me-2" />
                  Add Penalty
                </Button>
              </div>

              {/* Search */}
              <div className="mb-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search penalties..."
                  onKeyPress={handleSearch}
                  defaultValue={filterOptions.search}
                />
              </div>

              {agentPenalties.error && (
                <Alert variant="danger" className="mb-3">
                  {agentPenalties.error}
                </Alert>
              )}

              <Card>
                <div style={{ padding: "0" }}>
                  {agentPenalties.loading ? (
                    <div className="text-center p-4">
                      <Spinner animation="border" />
                      <p className="mt-2">Loading penalties...</p>
                    </div>
                  ) : (
                    <Table striped bordered hover style={{ marginBottom: "0" }}>
                      <thead>
                        <tr>
                          <th>� Agent</th>
                          <th>🎯 Season</th>
                          <th>⚠️ Reason</th>
                          <th>📊 Pengurangan Points</th>
                          <th>📅 Date</th>
                          <th style={{ textAlign: "center", width: "150px" }}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {agentPenalties.data.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="text-center py-4">
                              No penalties found. Click "Add Penalty" to create
                              your first penalty.
                            </td>
                          </tr>
                        ) : (
                          agentPenalties.data.map((penalty, index) => (
                            <tr key={penalty.id || index}>
                              <td>
                                <span
                                  className="badge p-2"
                                  style={{
                                    backgroundColor:
                                      penalty.agent?.color || "#6c757d",
                                    color: getContrastColor(
                                      penalty.agent?.color
                                    ),
                                  }}
                                >
                                  {penalty.agent?.name || "Unknown Agent"}
                                </span>
                              </td>
                              <td>
                                {penalty.season?.name || "Unknown Season"}
                              </td>
                              <td style={{ maxWidth: "300px" }}>
                                {penalty.reason}
                              </td>
                              <td>{penalty.penalty_points}</td>
                              <td>
                                {new Date(
                                  penalty.penalty_date
                                ).toLocaleDateString()}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                <Button
                                  variant="outline-info"
                                  className="m-1"
                                  onClick={() => handleView(penalty)}
                                >
                                  <IconEye size={14} />
                                </Button>
                                <Button
                                  variant="outline-primary"
                                  className="m-1"
                                  onClick={() => handleEdit(penalty)}
                                >
                                  <IconEdit size={14} />
                                </Button>
                                <Button
                                  variant="outline-success"
                                  className="m-1"
                                  onClick={() => handlePrint(penalty)}
                                >
                                  <IconPrinter size={14} />
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  onClick={() => handleDelete(penalty.id)}
                                  className="m-1"
                                >
                                  <IconTrash size={14} />
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  )}
                </div>
              </Card>

              {/* Pagination */}
              {agentPenalties.data.length > 0 && (
                <div className="d-flex justify-content-center mt-3">
                  {Pagination}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Create/Edit Modal */}
        <Modal className="agent-rank-scope" show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingPenalty ? "Edit Agent Penalty" : "Add New Agent Penalty"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AgentProvider>
            <AgentPenaltyForm
              initialData={editingPenalty}
              onSuccess={handleFormSuccess}
              onError={handleFormError}
            />
            </AgentProvider>
          </Modal.Body>
        </Modal>

        {/* View Modal */}
        <Modal className="agent-rank-scope" show={showViewModal} onHide={handleCloseViewModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Agent Penalty Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {viewingPenalty && (
              <div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Agent:</strong>
                    <p>{viewingPenalty.agent?.name || "Unknown Agent"}</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Season:</strong>
                    <p>{viewingPenalty.season?.name || "Unknown Season"}</p>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Reason:</strong>
                    <p>{viewingPenalty.reason}</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Penalty Points:</strong>
                    <p>{viewingPenalty.penalty_points}</p>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Penalty Date:</strong>
                    <p>
                      {new Date(
                        viewingPenalty.penalty_date
                      ).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <strong>Notes:</strong>
                    <p>{viewingPenalty.notes || "N/A"}</p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <strong>Created At:</strong>
                    <p>
                      {viewingPenalty.created_at
                        ? new Date(viewingPenalty.created_at).toLocaleString()
                        : "N/A"}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <strong>Updated At:</strong>
                    <p>
                      {viewingPenalty.updated_at
                        ? new Date(viewingPenalty.updated_at).toLocaleString()
                        : "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseViewModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Print Component */}
        {printingPenalty && (
          <PrintAgentPenalty
            penalty={printingPenalty}
            refetch={refetch}
            onSuccess={() => setPrintingPenalty(null)}
            onError={() => setPrintingPenalty(null)}
          />
        )}
      </div>
    </LayoutAgentRank>
  );
}

export default function AgentRankPenalty() {
  return (
    <AgentPenaltyProvider>
      <AgentRankPenaltyContent />
    </AgentPenaltyProvider>
  );
}
